import { FC, Fragment, ReactNode } from "react"

import HighlightOffIcon from "@mui/icons-material/Close"
import { Dialog as MuiDialog, DialogContent, MobileStepper } from "@mui/material"
import type { DialogProps as MuiDialogProps } from "@mui/material"

import { Typography, Button } from "@synapse-analytics/synapse-ui"

import styles from "./VisionDialog.module.scss"

/**
 * Props for the stepper navigation buttons
 */
export interface StepperButtonProps {
  /** Callback function when button is clicked */
  onClick: () => void
  /** Whether the button is disabled */
  disabled?: boolean
  /** Whether the button is hidden */
  hidden?: boolean
  /** Text to display on the button */
  text: string
  /** Tooltip text to show on hover */
  tooltip?: string
}

/**
 * Configuration for the dialog's stepper component
 */
export interface StepperConfig {
  /** Total number of steps in the stepper */
  totalSteps: number
  /** Current active step index */
  activeStep: number
  /** Callback when step changes */
  onStepChange?: (step: number) => void
  /** Configuration for the next button */
  nextButton?: StepperButtonProps
  /** Configuration for the back button */
  backButton?: StepperButtonProps
  /** Whether to show the stepper */
  show?: boolean
}

/**
 * Props for the VisionDialog component
 */
export interface DialogProps {
  /** Whether the dialog is open */
  isOpen: boolean
  /** Callback when dialog is closed */
  onClose: (e?: React.SyntheticEvent) => void
  /** Dialog title, can be string or ReactNode */
  title: string | ReactNode
  /** Dialog content */
  children: ReactNode
  /** Maximum width of the dialog */
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl"
  /** Minimum width of the dialog */
  minWidth?: string
  /** Whether to show the close icon */
  showCloseIcon?: boolean
  /** Stepper configuration */
  stepper?: StepperConfig
  /** Custom actions to display at the bottom */
  customActions?: ReactNode
  /** Additional CSS class name */
  className?: string
  /** Keyboard event handler */
  handleKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void
  /** Whether to disable closing by clicking outside or pressing escape */
  disableOuterExit?: boolean
  /** Whether to make the header sticky and enable content scrolling */
  hasStickyHeader?: boolean
}

/**
 * A customizable dialog component with optional stepper navigation
 * @param props - Dialog configuration props
 * @returns A dialog component with optional stepper navigation
 */
export const VisionDialog: FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = "sm",
  showCloseIcon = true,
  stepper,
  customActions,
  className,
  minWidth,
  handleKeyDown,
  disableOuterExit = true,
  hasStickyHeader = false,
}) => {
  /**
   * Renders the stepper navigation component if configured
   * @returns The stepper component or null if not needed
   */
  const renderStepper = () => {
    if (!stepper?.totalSteps || stepper.totalSteps <= 1 || stepper.show === false || !stepper) return null

    return (
      <MobileStepper
        variant="dots"
        steps={stepper.totalSteps}
        position="static"
        activeStep={stepper.activeStep}
        className={styles.stepper}
        nextButton={
          stepper.nextButton && (
            <Button
              size="regular"
              variant="primary"
              onClick={stepper.nextButton.onClick}
              disabled={stepper.nextButton.disabled}
              style={{ display: stepper.nextButton.hidden ? "none" : "flex" }}
              tooltip={stepper.nextButton.tooltip}
            >
              {stepper.nextButton.text}
            </Button>
          )
        }
        backButton={
          stepper.backButton && (
            <Button
              size="regular"
              variant="secondary"
              onClick={stepper.backButton.onClick}
              disabled={stepper.backButton.disabled}
              style={{ display: stepper.backButton.hidden ? "none" : "flex" }}
            >
              {stepper.backButton.text}
            </Button>
          )
        }
      />
    )
  }

  /**
   * Handles dialog close events
   * @param event - The synthetic event
   * @param reason - The reason for closing
   */
  const handleClose: MuiDialogProps["onClose"] = (
    event: React.SyntheticEvent,
    reason: "backdropClick" | "escapeKeyDown"
  ) => {
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown") && disableOuterExit) return
    event?.stopPropagation()
    onClose(event)
  }

  return (
    <MuiDialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth
      disableEscapeKeyDown={disableOuterExit}
      onKeyDown={handleKeyDown}
      onClick={(e) => e.stopPropagation()}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-content"
      PaperProps={{
        sx: {
          minWidth: minWidth,
        },
      }}
    >
      <DialogContent className={`${styles.wrapper} ${hasStickyHeader ? styles.wrapperSticky : ""} ${className}`}>
        <div className={`${styles.dialogHeader} ${hasStickyHeader ? styles.dialogHeaderSticky : ""}`}>
          {typeof title === "string" ? <Typography variant="h2-bold">{title}</Typography> : title}
          {showCloseIcon && <HighlightOffIcon onClick={onClose} className={styles.iconContainer} />}
        </div>

        {hasStickyHeader ? (
          <Fragment>
            <div className={styles.dialogContent}>{children}</div>
            {(customActions || renderStepper()) && (
              <div className={styles.dialogFooter}>{customActions || renderStepper()}</div>
            )}
          </Fragment>
        ) : (
          <Fragment>
            {children}
            {customActions || renderStepper()}
          </Fragment>
        )}
      </DialogContent>
    </MuiDialog>
  )
}

export default VisionDialog
